/** @jsxImportSource preact */
import { useState, useEffect, useRef } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { DateTime } from 'luxon'

type EticketFile = {
  id: number
  name: string
  update_time: string
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

export function Etickets() {
  const [bookingId, setBookingId] = useState('')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [etickets, setEtickets] = useState<EticketFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [fetchingEtickets, setFetchingEtickets] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<FormStatus>('idle')
  const [uploadErrorMessage, setUploadErrorMessage] = useState('')
  const [generateStatus, setGenerateStatus] = useState<FormStatus>('idle')
  const [generateErrorMessage, setGenerateErrorMessage] = useState('')
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropAreaRef = useRef<HTMLDivElement>(null)

  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()

  // Effect to extract booking ID from Front context
  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  // Fetch etickets when bookingId changes
  useEffect(() => {
    if (bookingId) {
      fetchEtickets()
    } else {
      setEtickets([])
    }
  }, [bookingId])

  // Set up drag and drop event handlers
  useEffect(() => {
    const dropArea = dropAreaRef.current
    if (!dropArea) return

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)
    }

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
    }

    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (e.dataTransfer?.files) {
        handleFiles(Array.from(e.dataTransfer.files))
      }
    }

    dropArea.addEventListener('dragover', handleDragOver)
    dropArea.addEventListener('dragenter', handleDragEnter)
    dropArea.addEventListener('dragleave', handleDragLeave)
    dropArea.addEventListener('drop', handleDrop)

    return () => {
      dropArea.removeEventListener('dragover', handleDragOver)
      dropArea.removeEventListener('dragenter', handleDragEnter)
      dropArea.removeEventListener('dragleave', handleDragLeave)
      dropArea.removeEventListener('drop', handleDrop)
    }
  }, [])

  const fetchEtickets = async () => {
    if (!bookingId.trim()) {
      return
    }

    setFetchingEtickets(true)
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/etickets`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch etickets')
      }

      const data = await response.json()
      setEtickets(data)
    } catch (error) {
      console.error('Error fetching etickets:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch etickets')
      setEtickets([])
    } finally {
      setFetchingEtickets(false)
    }
  }

  const handleFileInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  const handleFiles = (files: File[]) => {
    // Filter for only PDF files
    const pdfFiles = files.filter(file => file.type === 'application/pdf')

    if (pdfFiles.length === 0) {
      setUploadErrorMessage('Only PDF files are accepted')
      return
    }

    if (pdfFiles.length !== files.length) {
      setUploadErrorMessage('Some files were rejected. Only PDF files are accepted.')
    } else {
      setUploadErrorMessage('')
    }

    setSelectedFiles(prevFiles => [...prevFiles, ...pdfFiles])
  }

  const resetUploadForm = () => {
    setSelectedFiles([])
    setUploadStatus('idle')
    setUploadErrorMessage('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (!bookingId.trim()) {
      setUploadErrorMessage('Booking ID is required')
      return
    }

    if (selectedFiles.length === 0) {
      setUploadErrorMessage('Please select files to upload')
      return
    }

    setUploadStatus('loading')
    setUploadErrorMessage('')

    try {
      const formData = new FormData()

      // Append each file to the form data with the same field name 'files'
      selectedFiles.forEach(file => {
        formData.append('files', file)
      })

      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/etickets`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to upload etickets')
      }

      setUploadStatus('success')
      // Refresh the list of etickets
      fetchEtickets()
      // Reset the form
      resetUploadForm()
    } catch (error) {
      console.error('Error uploading etickets:', error)
      setUploadStatus('error')
      setUploadErrorMessage(error instanceof Error ? error.message : 'Failed to upload etickets')
    }
  }

  const confirmDelete = (id: number) => {
    setDeleteId(id)
    setShowDeleteConfirm(true)
  }

  const cancelDelete = () => {
    setDeleteId(null)
    setShowDeleteConfirm(false)
  }

  const confirmGenerate = () => {
    setShowGenerateConfirm(true)
  }

  const cancelGenerate = () => {
    setShowGenerateConfirm(false)
  }

  const handleDelete = async () => {
    if (!bookingId.trim() || deleteId === null) {
      return
    }

    setFormStatus('loading')
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/etickets/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete eticket')
      }

      setFormStatus('success')
      // Refresh the list of etickets
      fetchEtickets()
      // Close the confirmation dialog
      setShowDeleteConfirm(false)
      setDeleteId(null)
    } catch (error) {
      console.error('Error deleting eticket:', error)
      setFormStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to delete eticket')
    }
  }

  const formatDate = (dateString: string) => {
    return DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
      .setZone('local')
      .toLocaleString(DateTime.DATETIME_FULL)
  }

  const handleGenerate = async () => {
    if (!bookingId.trim()) {
      setGenerateErrorMessage('Booking ID is required')
      return
    }

    setGenerateStatus('loading')
    setGenerateErrorMessage('')
    setShowGenerateConfirm(false)

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/eticket/snapshots`, {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate etickets')
      }

      setGenerateStatus('success')
      fetchEtickets()

      setTimeout(() => {
        setGenerateStatus('idle')
      }, 3000)
    } catch (error) {
      console.error('Error generating etickets:', error)
      setGenerateStatus('error')
      setGenerateErrorMessage(error instanceof Error ? error.message : 'Failed to generate etickets')
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">E-Tickets</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">E-Tickets</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="bookingId"
            type="text"
            value={bookingId}
            onChange={e => setBookingId((e.target as HTMLInputElement).value)}
            className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
            placeholder="Enter Booking ID"
            required
          />
          {fetchingEtickets && (
            <div className="absolute right-3 top-2">
              <svg
                className="animate-spin h-5 w-5 text-indigo-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
        {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
        {bookingId && isFrontApp && (
          <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
        )}
      </div>

      <div className="mt-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Upload E-Tickets</h2>

        <div
          ref={dropAreaRef}
          className={`p-6 border-2 border-dashed rounded-md mb-4 ${
            isDragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 bg-gray-50'
          }`}
        >
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              ></path>
            </svg>
            <p className="mt-1 text-sm text-gray-600">
              Drag and drop PDF files here, or
              <button
                type="button"
                className="ml-1 text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                onClick={() => fileInputRef.current?.click()}
              >
                browse
              </button>
            </p>
            <p className="mt-1 text-xs text-gray-500">Only PDF files are accepted</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="application/pdf"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Selected files:</h3>
              <button
                type="button"
                onClick={() => setSelectedFiles([])}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Clear All
              </button>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              {selectedFiles.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-xs text-red-600 hover:text-red-800 ml-2"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploadErrorMessage && <div className="p-3 bg-red-50 text-red-700 rounded-md mb-4">{uploadErrorMessage}</div>}

        {uploadStatus === 'success' && (
          <div className="p-3 bg-green-50 text-green-700 rounded-md mb-4">Files uploaded successfully!</div>
        )}

        <div className="flex space-x-4">
          <button
            type="button"
            onClick={resetUploadForm}
            disabled={uploadStatus === 'loading' || selectedFiles.length === 0}
            className="w-1/3 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploadStatus === 'loading' || selectedFiles.length === 0 || !bookingId}
            className="w-2/3 px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadStatus === 'loading' ? 'Uploading...' : 'Upload Files'}
          </button>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold text-gray-800">E-Ticket Files</h2>
          <button
            type="button"
            onClick={confirmGenerate}
            disabled={generateStatus === 'loading' || !bookingId}
            className="px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generateStatus === 'loading' ? 'Generating...' : 'Generate E-Tickets'}
          </button>
        </div>

        {generateErrorMessage && (
          <div className="p-3 bg-red-50 text-red-700 rounded-md mb-4">{generateErrorMessage}</div>
        )}

        {generateStatus === 'success' && (
          <div className="p-3 bg-green-50 text-green-700 rounded-md mb-4">E-tickets generated successfully!</div>
        )}

        {fetchingEtickets ? (
          <div className="p-4 bg-gray-50 rounded-md flex justify-center items-center">
            <svg
              className="animate-spin h-6 w-6 text-indigo-600 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Loading e-tickets...</span>
          </div>
        ) : (
          <>
            {etickets.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-md text-center">
                {bookingId ? 'No e-tickets found for this booking.' : 'Enter a booking ID to view e-tickets.'}
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        File Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Last Updated
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {etickets.map(eticket => (
                      <tr key={eticket.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{eticket.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(eticket.update_time)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => confirmDelete(eticket.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {formStatus === 'error' && errorMessage && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md mb-4">{errorMessage}</div>
      )}

      {/* Generate confirmation modal */}
      {showGenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm E-Ticket Generation</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will delete all existing e-ticket files and generate new ones. Are you sure you want to continue?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={cancelGenerate}
                className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                className="px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this e-ticket? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {formStatus === 'loading' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
